import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../../components/feature/Navbar';
import { Footer } from '../../components/feature/Footer';

export default function AboutPage() {
  const credibilityPoints = [
    'Professional quality websites that stand out',
    'Transparent pricing with no hidden fees',
    'Fast delivery - websites live in 7-14 days',
    'Canadian-owned and operated business',
    'Uses modern tools and best practices',
    'Expert in SEO, AEO, and GEO optimization'
  ];

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Integrity',
      description: 'We believe in honest communication and transparent business practices'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation',
      description: 'We stay ahead with the latest web design trends and technologies'
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Customer Focus',
      description: 'Your success is our success - we prioritize your business goals'
    },
    {
      icon: 'ri-award-line',
      title: 'Excellence',
      description: 'We deliver high-quality work that exceeds expectations'
    }
  ];

  const canadaProvinces = [
    'British Columbia', 'Alberta', 'Saskatchewan', 'Manitoba',
    'Ontario', 'Quebec', 'New Brunswick', 'Nova Scotia',
    'Prince Edward Island', 'Newfoundland and Labrador'
  ];

  const worldwideRegions = [
    { icon: 'ri-earth-line', name: 'Europe', description: 'UK, Germany, France & more' },
    { icon: 'ri-earth-line', name: 'Asia Pacific', description: 'Australia, Singapore, Japan' },
    { icon: 'ri-earth-line', name: 'South Asia', description: 'India, Philippines, Thailand' },
    { icon: 'ri-earth-line', name: 'Middle East', description: 'UAE, Saudi Arabia, Qatar' },
    { icon: 'ri-earth-line', name: 'Latin America', description: 'Mexico, Brazil, Argentina' },
    { icon: 'ri-earth-line', name: 'Africa', description: 'South Africa, Nigeria, Kenya' },
    { icon: 'ri-earth-line', name: 'Caribbean', description: 'Jamaica, Bahamas, Trinidad' }
  ];

  useEffect(() => {
    // Update page title and meta tags
    document.title = 'About Us | BusinessWebsitePro';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about BusinessWebsitePro. Vancouver-based web design agency committed to transparency, quality, and client ownership.');
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Meet the Experts | Our Anti-Hidden Fee & Full Ownership Mission | Website Design E-E-A-T | BWP');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Meet the experts behind BusinessWebsitePro. Learn about our mission to end hidden fees and vendor lock-in with guaranteed full website ownership.');
    }

    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', 'Meet the Experts | Our Anti-Hidden Fee & Full Ownership Mission | Website Design E-E-A-T | BWP');
    }

    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', 'Meet the experts behind BusinessWebsitePro. Learn about our mission to end hidden fees and vendor lock-in with guaranteed full website ownership.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/6f2650934939a0512899965bcaf0d9d4.jpg"
            alt="BusinessWebsitePro team collaboration"
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 drop-shadow-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
            About BusinessWebsitePro
          </h1>
        </div>
      </section>

      {/* Slogan Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
            Our Core Philosophy: Transparent Pricing and Full Website Ownership
          </h2>
        </div>
      </section>

      {/* Credibility Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Image */}
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20web%20design%20team%20working%20together%20in%20modern%20office%2C%20collaborative%20workspace%20with%20computers%20and%20design%20tools%2C%20diverse%20team%20members%20discussing%20website%20projects%2C%20bright%20natural%20lighting%2C%20creative%20business%20environment&width=800&height=600&seq=about-team-image&orientation=landscape" 
                alt="Our team at work" 
                className="w-full h-auto rounded-3xl shadow-2xl"
              />
            </div>
            
            {/* Right Column - Content */}
            <div>
              <p className="text-sm uppercase tracking-wider text-[#0d3b66] font-semibold mb-4">WHY CHOOSE US</p>
              <h2 className="text-4xl font-bold text-gray-900 mb-8">
                Professional Quality, Transparent Pricing
              </h2>
              
              <div className="space-y-6">
                {credibilityPoints.map((point, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-xl text-green-600"></i>
                    </div>
                    <p className="text-xl text-gray-700 leading-relaxed">{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-32 bg-[#F8F9FA]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">Our Mission</h2>
          <p className="text-2xl text-gray-700 leading-relaxed mb-12">
            We empower small businesses across North America and worldwide to establish a strong online presence without breaking the bank. Every business deserves a professional website that attracts customers and drives growth.
          </p>
          <p className="text-xl text-gray-600 leading-relaxed">
            We combine cutting-edge design, proven SEO strategies, and affordable pricing to help entrepreneurs compete in the digital marketplace. Your success is our passion.
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-lg text-gray-600">The principles that guide everything we do</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className={`${value.icon} text-3xl text-[#0d3b66]`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Worldwide Customers Section */}
      <section className="py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Serving Clients Worldwide</h2>
            <p className="text-lg text-gray-600">Proudly serving businesses across Canada, United States, and around the globe</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {/* Canada */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="text-5xl">🇨🇦</div>
                <h3 className="text-3xl font-bold text-gray-900">Canada</h3>
              </div>
              <p className="text-base text-gray-600 mb-6">Primary service area - All provinces and territories</p>
              <div className="grid grid-cols-1 gap-3">
                {canadaProvinces.map((province, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <i className="ri-map-pin-line text-[#0d3b66]"></i>
                    <span className="text-sm text-gray-700">{province}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* United States */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="text-5xl">🇺🇸</div>
                <h3 className="text-3xl font-bold text-gray-900">United States</h3>
              </div>
              <p className="text-base text-gray-600 mb-6">Secondary service area - All 50 states</p>
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-[#0d3b66]"></i>
                  <span className="text-sm text-gray-700">All 50 states covered</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-[#0d3b66]"></i>
                  <span className="text-sm text-gray-700">All time zones supported</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-customer-service-line text-[#0d3b66]"></i>
                  <span className="text-sm text-gray-700">Dedicated US client support</span>
                </div>
              </div>
              
              {/* Map Illustration */}
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=simplified%20map%20of%20United%20States%20with%20location%20pins%2C%20clean%20minimalist%20cartography%20style%2C%20professional%20business%20illustration%2C%20light%20background%2C%20modern%20design&width=600&height=400&seq=us-map&orientation=landscape" 
                  alt="United States service map" 
                  className="w-full h-auto rounded-2xl"
                />
              </div>
            </div>

            {/* Philippines & India */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="mb-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-5xl">🇵🇭</div>
                  <h3 className="text-3xl font-bold text-gray-900">Philippines</h3>
                </div>
                <p className="text-base text-gray-600 mb-4">Growing market - Full service coverage</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <i className="ri-map-pin-line text-[#0d3b66]"></i>
                    <span className="text-sm text-gray-700">Manila, Cebu, Davao</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-global-line text-[#0d3b66]"></i>
                    <span className="text-sm text-gray-700">All regions supported</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-5xl">🇮🇳</div>
                  <h3 className="text-3xl font-bold text-gray-900">India</h3>
                </div>
                <p className="text-base text-gray-600 mb-4">Expanding market - Nationwide coverage</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <i className="ri-map-pin-line text-[#0d3b66]"></i>
                    <span className="text-sm text-gray-700">Mumbai, Delhi, Bangalore</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-global-line text-[#0d3b66]"></i>
                    <span className="text-sm text-gray-700">All major cities covered</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-lg mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="text-5xl">🌍</div>
              <h3 className="text-3xl font-bold text-gray-900">Other Worldwide Regions</h3>
            </div>
            <p className="text-base text-gray-600 mb-6">Global service area - International clients welcome</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {worldwideRegions.map((region, index) => (
                <div key={index} className="flex items-start gap-3">
                  <i className={`${region.icon} text-xl text-[#0d3b66] mt-1`}></i>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{region.name}</p>
                    <p className="text-xs text-gray-600">{region.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Global Stats */}
          <div className="bg-white rounded-3xl p-12 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-[#0d3b66] mb-2">50+</p>
                <p className="text-base text-gray-600">Countries Served</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-[#0d3b66] mb-2">24/7</p>
                <p className="text-base text-gray-600">Global Support</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-[#0d3b66] mb-2">15+</p>
                <p className="text-base text-gray-600">Languages Supported</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-[#0d3b66] mb-2">100%</p>
                <p className="text-base text-gray-600">Remote Friendly</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            {/* Left Column - Photo */}
            <div className="lg:col-span-2 flex justify-center">
              <div className="relative">
                <div className="w-80 h-80 rounded-full border-8 border-white shadow-2xl overflow-hidden relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20Indian%20business%20founder%20portrait%2C%20confident%20Indian%20entrepreneur%20in%20modern%20office%20setting%2C%20professional%20headshot%20with%20natural%20lighting%2C%20Indian%20male%20business%20professional%20in%20formal%20attire%2C%20clean%20background%2C%20successful%20tech%20entrepreneur&width=400&height=400&seq=founder-portrait-indian&orientation=squarish" 
                    alt="Founder" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0d3b66]/30 via-transparent to-transparent"></div>
                </div>
              </div>
            </div>
            
            {/* Right Column - Content */}
            <div className="lg:col-span-3">
              <p className="text-sm uppercase tracking-wider text-[#0d3b66] font-semibold mb-4">FOUNDER</p>
              <h3 className="text-4xl font-bold text-gray-900 mb-3">Meet Our Founder</h3>
              <p className="text-xl text-[#0d3b66] font-semibold mb-6">Founder &amp; Lead Designer</p>
              
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  With over a decade of experience in web design and digital marketing, our founder started BusinessWebsitePro with a clear mission: make professional web design accessible to every small business.
                </p>
                <p>
                  Having worked with hundreds of entrepreneurs across Canada and the United States, we understand the unique challenges small businesses face when establishing their online presence.
                </p>
                <p>
                  Our commitment to quality, affordability, and customer success has helped countless businesses grow their online presence and achieve their goals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}